import React, { useEffect, useState } from 'react';
import { api, uploadFile } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { VERIFICATION_DOCS } from '@/lib/constants';
import { BadgeCheck, Upload, ShieldCheck, FileText, Trash2 } from 'lucide-react';

const VerificationCenter = () => {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ document_type: '', document_url: '', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get('/verification/mine').then(({ data }) => setHistory(data));
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const res = await uploadFile(f, 'pdf');
      setForm({ ...form, document_url: res.public_url });
      toast.success('Document uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!form.document_type || !form.document_url) return toast.error('Pick document type and upload the file');
    setSubmitting(true);
    try {
      await api.post('/verification/submit', form);
      toast.success('Submitted — admin/moderator will review');
      setForm({ document_type: '', document_url: '', notes: '' });
      await refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>Verification center</h1>
        <p className='text-muted-foreground text-sm'>Submit business documents to earn a Verified badge and increase buyer trust.</p>
      </div>

      <Card className='p-6 flex items-start gap-4'>
        <div className='h-12 w-12 rounded-lg bg-secondary flex items-center justify-center'>
          <BadgeCheck className='h-6 w-6 text-[hsl(var(--verified))]' />
        </div>
        <div>
          <div className='font-semibold'>Current level: {user?.verification_level?.replace(/_/g, ' ')}</div>
          <div className='text-sm text-muted-foreground'>Levels: none → email verified → documents submitted → company verified</div>
        </div>
      </Card>

      <Card className='p-6 space-y-4'>
        <div className='font-semibold'>Submit a new verification document</div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Document type</Label>
            <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
              <SelectTrigger data-testid='verification-doc-type'><SelectValue placeholder='Select document type' /></SelectTrigger>
              <SelectContent>{VERIFICATION_DOCS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Upload PDF</Label>
            {form.document_url ? (
              <div className='rounded-md border bg-secondary/30 p-3 space-y-2' data-testid='verification-file-preview'>
                <div className='flex items-center gap-2 text-sm'>
                  <FileText className='h-4 w-4 text-primary shrink-0' />
                  <a
                    href={form.document_url.startsWith('http') ? form.document_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.document_url}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline font-medium truncate'
                    data-testid='verification-file-view'
                  >
                    View uploaded document
                  </a>
                </div>
                <div className='flex items-center gap-2 flex-wrap'>
                  <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 cursor-pointer text-xs hover:bg-secondary'>
                    <Upload className='h-3.5 w-3.5' /> Replace
                    <input type='file' className='hidden' accept='.pdf,image/*' onChange={upload} disabled={uploading} data-testid='verification-file-replace' />
                  </label>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setForm({ ...form, document_url: '' })}
                    disabled={uploading || submitting}
                    className='h-7 text-xs text-destructive hover:text-destructive hover:bg-red-50 border-red-300'
                    data-testid='verification-file-remove'
                  >
                    <Trash2 className='h-3.5 w-3.5 mr-1' /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
                  <Upload className='h-4 w-4' /> Choose file
                  <input type='file' className='hidden' accept='.pdf,image/*' onChange={upload} disabled={uploading} data-testid='verification-file-upload' />
                </label>
                {uploading && <span className='text-xs text-muted-foreground'>Uploading…</span>}
              </div>
            )}
          </div>
        </div>
        <div>
          <Label>Notes (optional)</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className='flex justify-end'>
          <Button onClick={submit} disabled={submitting || uploading} data-testid='verification-submit'>
            <ShieldCheck className='h-4 w-4 mr-1.5' /> {submitting ? 'Submitting…' : 'Submit for review'}
          </Button>
        </div>
      </Card>

      <Card className='p-6'>
        <div className='font-semibold mb-3'>Submission history</div>
        {history.length === 0 ? (
          <div className='text-sm text-muted-foreground'>No submissions yet.</div>
        ) : (
          <div className='space-y-2'>
            {history.map(h => (
              <div key={h.id} className='border-b py-2 text-sm space-y-2 last:border-b-0'>
                <div className='flex items-center justify-between gap-3'>
                  <div>
                    <div className='font-medium'>{h.document_type}</div>
                    <div className='text-xs text-muted-foreground'>{new Date(h.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${h.status === 'approved' ? 'status-approved' : h.status === 'rejected' ? 'status-rejected' : 'status-pending'}`} data-testid={`verification-status-${h.id}`}>{h.status}</span>
                </div>
                {h.status === 'rejected' && h.review_reason && (
                  <div
                    className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-900'
                    data-testid={`verification-reject-reason-${h.id}`}
                  >
                    <div className='font-semibold mb-0.5'>Reason for rejection</div>
                    <div className='whitespace-pre-wrap'>{h.review_reason}</div>
                    <div className='mt-1 text-[11px] text-red-800/80'>
                      Please correct the above and re-submit a fresh document.
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default VerificationCenter;
